import { useMutation, useQuery, useQueryClient } from "react-query";
import { useMatch } from "react-router-dom";

import Alert from "components/atoms/alert/Alert";

import {
    deleteReference,
    getReference,
    getReferences,
    patchReference,
    postReference,
    postReferenceAddFromSource,
    postReferenceAddWithBibtex,
    postBulkReference,
    putBibJson,
    getTagRecommendation,
} from "utils/request/reference";

const useCurrentSpaceUuid = () => {
    const match = useMatch("/:space_uuid/*");
    return match?.params?.space_uuid ?? "";
};

export const useReferenceList = (params, options = {}) => {
    const space_uuid = useCurrentSpaceUuid();
    const { enabled: paramsEnabled, ...requestParams } = params || {};
    const enabled = options.enabled ?? paramsEnabled ?? true;

    return useQuery(["referenceList", space_uuid, requestParams], () => getReferences(space_uuid, requestParams), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        keepPreviousData: true,
        ...options,
        enabled,
    });
};

export const useReference = (params) => {
    const space_uuid = useCurrentSpaceUuid();
    return useQuery(["reference", space_uuid, params?.reference_no], () => getReference(space_uuid, params), {
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        enabled: !!params?.reference_no,
    });
};

export const useReferenceCreate = (options = {}) => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();
    const { onSuccess, onError, ...mutationOptions } = options;

    return useMutation(
        (variables) => {
            if (variables.addWithBibtex) {
                return postReferenceAddWithBibtex(space_uuid, variables.body);
            }
            if (variables.addFromSource) {
                return postReferenceAddFromSource(space_uuid, { body: variables.body });
            }
            return postReference(space_uuid, variables);
        },
        {
            ...mutationOptions,
            onSuccess: (data, variables, context) => {
                queryClient.invalidateQueries({ queryKey: "reference" });
                queryClient.invalidateQueries({ queryKey: "referenceList" });
                queryClient.invalidateQueries({ queryKey: "volume" });
                onSuccess?.(data, variables, context);
            },
            onError: (error, variables, context) => {
                if (onError) {
                    onError(error, variables, context);
                    return;
                }
                if (error.response.status === 400) {
                    if (error.response.data.bib_json) {
                        if (error.response.data.bib_json.citation_key) {
                            Alert("warn", "에러", error.response.data.bib_json.citation_key);
                        } else {
                            Alert("warn", "에러", error.response.data.bib_json);
                        }
                    } else if (error.response.data.maximum_references) {
                        Alert("warn", "에러", error.response.data.maximum_references);
                    } else {
                        Alert("warn", "에러", "참고문헌 추가에 실패했습니다.");
                    }
                } else {
                    Alert("warn", "에러", "서버에 오류가 발생했습니다.");
                }
            },
        },
    );
};

export const useReferenceBulkCreate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();

    return useMutation((body) => postBulkReference(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "volume" });
        },
        onError: (error, variables, context) => {
            if (error.response.status !== 400) {
                Alert("warn", "에러", "서버에 오류가 발생했습니다.");
            }
        },
    });
};

export const useReferenceUpdate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((body) => patchReference(space_uuid, body), {
        mutationKey: "referenceUpdate",
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "research" });
            queryClient.invalidateQueries({ queryKey: "bookmarkedReferences" });
            queryClient.invalidateQueries({ queryKey: "volume" });
        },
        onError: (error, variables, context) => {
            if (error.response.status === 400) {
                Alert("warn", "에러", error.response?.data?.bib_json || "참고문헌 수정에 실패했습니다.");
            } else if (error.response.status === 500) {
                console.log(error.response);
                Alert("warn", "에러", "서버에 오류가 발생했습니다.");
            }
        },
    });
};

export const useBibJsonUpdate = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((body) => putBibJson(space_uuid, body), {
        mutationKey: "bibJsonUpdate",
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "research" });
        },
    });
};

export const useReferenceDelete = () => {
    const queryClient = useQueryClient();
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((body) => deleteReference(space_uuid, body), {
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: "reference" });
            queryClient.invalidateQueries({ queryKey: "research" });
            queryClient.invalidateQueries({ queryKey: "tags" });
            queryClient.invalidateQueries({ queryKey: "bookmarkedReferences" });
            queryClient.invalidateQueries({ queryKey: "referenceList" });
            queryClient.invalidateQueries({ queryKey: "volume" });
        },
    });
};

export const useTagRecommendation = () => {
    const space_uuid = useCurrentSpaceUuid();
    return useMutation((body) => getTagRecommendation(space_uuid, body), {
        mutationKey: "tagRecommendation",
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
    });
};
